import { getApiBase, getApiRoot } from './apiBase';

// ── API-backed storage (base from apiBase.js — ignores mistaken localhost env on Vercel) ──

function urlsForPath(path) {
  const root = getApiRoot().replace(/\/+$/, '');
  const api = getApiBase().replace(/\/+$/, '');
  // Try both forms so auth keeps working even if a stale/misconfigured base appears.
  return Array.from(new Set([
    `${api}${path}`,
    `${root}${path}`,
    `${root}/api${path}`,
  ]));
}

// ── Token helpers ─────────────────────────────────────────────────
export function getToken()        { return sessionStorage.getItem('lkps_token'); }
export function setToken(t)       { sessionStorage.setItem('lkps_token', t); }
export function clearToken()      { sessionStorage.removeItem('lkps_token'); }

async function req(method, path, body) {
  const token = getToken();
  const candidates = urlsForPath(path);
  let lastErr = null;

  for (const url of candidates) {
    const res = await fetch(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: 'Bearer ' + token } : {}),
      },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
    const data = await res.json().catch(() => ({}));
    if (res.ok) return data;

    const msg = data.error || res.statusText || '';
    lastErr = new Error(msg);
    // If one base gives route miss, try the alternate base.
    if (res.status === 404 && /route not found/i.test(msg)) continue;
    throw lastErr;
  }

  throw lastErr || new Error('Request failed');
}

// ── Auth ──────────────────────────────────────────────────────────

export async function apiLogin(username, password) {
  const data = await req('POST', '/auth/login', { username, password });
  setToken(data.token);
  return data.user; // { id, username, name, role, photo }
}

export async function apiTeacherLogin(username, password, sessionId) {
  const data = await req('POST', '/auth/teacher-login', { username, password, sessionId });
  setToken(data.token);
  return data.teacher;
}

export async function apiParentLogin(username, password) {
  const data = await req('POST', '/auth/parent-login', { username, password });
  setToken(data.token);
  return data.student;
}

// ── Sessions ──────────────────────────────────────────────────────

export async function getSessions() {
  const list = await req('GET', '/sessions');
  // normalise: backend uses { sid, name, year, created } — app uses { id, name, year, created }
  return list.map(s => ({ ...s, id: s.sid }));
}

export async function createSession(sid, name, year, carryFrom) {
  const s = await req('POST', '/sessions', { sid, name, year, carryFrom });
  return { ...s, id: s.sid };
}

export async function updateSession(sid, name, year) {
  const s = await req('PATCH', '/sessions/' + sid, { name, year });
  return { ...s, id: s.sid };
}

export async function deleteSession(sid) {
  await req('DELETE', '/sessions/' + sid);
}

// ── Session data (settings, classes, tt, slots, fstr, events) ─────

export async function loadSessionConfig(sessionId) {
  return req('GET', '/sessions/' + sessionId + '/data');
}

export async function saveSessionConfig(sessionId, patch) {
  return req('PATCH', '/sessions/' + sessionId + '/data', patch);
}

// ── Students ──────────────────────────────────────────────────────

export async function getStudents(sessionId) {
  return req('GET', '/sessions/' + sessionId + '/students');
}

export async function createStudent(sessionId, student) {
  return req('POST', '/sessions/' + sessionId + '/students', student);
}

export async function updateStudent(sessionId, sid, patch) {
  return req('PATCH', '/sessions/' + sessionId + '/students/' + sid, patch);
}

export async function markIdCardGenerated(sessionId, sid, { theme, year, prin, phone }) {
  return req('PATCH', '/sessions/' + sessionId + '/students/' + sid + '/id-card-generated', { theme, year, prin, phone });
}

export async function saveReportCard(sessionId, sid, html) {
  return req('PATCH', '/sessions/' + sessionId + '/students/' + sid + '/report-card', { html });
}

export async function uploadStudentDocument(sessionId, sid, doc) {
  return req('POST', '/sessions/' + sessionId + '/students/' + sid + '/documents', doc);
}

export async function adminUploadStudentDocument(sessionId, sid, doc) {
  return req('POST', '/sessions/' + sessionId + '/students/' + sid + '/documents', { ...doc, uploadedBy: 'admin' });
}

export async function getStaffAttendance(sessionId, date) {
  return req('GET', '/sessions/' + sessionId + '/staff-attendance' + (date ? '?date='+date : ''));
}

export async function saveStaffAttendance(sessionId, date, records) {
  return req('POST', '/sessions/' + sessionId + '/staff-attendance', { date, records });
}

export async function getStaffAttendanceReport(sessionId, tid) {
  return req('GET', '/sessions/' + sessionId + '/staff-attendance/report' + (tid ? '?tid='+tid : ''));
}

export async function reviewStudentDocument(sessionId, sid, docId, status, rejectReason) {
  return req('PATCH', '/sessions/' + sessionId + '/students/' + sid + '/documents/' + docId, { status, rejectReason });
}

export async function getStudentDocuments(sessionId, sid) {
  return req('GET', '/sessions/' + sessionId + '/students/' + sid + '/documents');
}

export async function getStudentDocument(sessionId, sid, docId) {
  return req('GET', '/sessions/' + sessionId + '/students/' + sid + '/documents/' + docId);
}

// Parent-specific document routes (uses parent JWT, no session/sid params needed)
export async function getMyDocuments() {
  return req('GET', '/auth/student-documents');
}

export async function getMyDocument(docId) {
  return req('GET', '/auth/student-document/' + docId);
}

export async function uploadMyDocument(doc) {
  return req('POST', '/auth/student-upload-document', doc);
}

export async function deleteStudent(sessionId, sid) {
  return req('DELETE', '/sessions/' + sessionId + '/students/' + sid);
}

// ── Teachers ──────────────────────────────────────────────────────

export async function getTeachers(sessionId) {
  return req('GET', '/sessions/' + sessionId + '/teachers');
}

export async function createTeacher(sessionId, teacher) {
  return req('POST', '/sessions/' + sessionId + '/teachers', teacher);
}

export async function updateTeacher(sessionId, tid, patch) {
  return req('PATCH', '/sessions/' + sessionId + '/teachers/' + tid, patch);
}

export async function deleteTeacher(sessionId, tid) {
  return req('DELETE', '/sessions/' + sessionId + '/teachers/' + tid);
}

// ── Payments ──────────────────────────────────────────────────────

export async function getPayments(sessionId) {
  return req('GET', '/sessions/' + sessionId + '/payments');
}

export async function createPayment(sessionId, payment) {
  return req('POST', '/sessions/' + sessionId + '/payments', payment);
}

export async function updatePayment(sessionId, pid, patch) {
  return req('PATCH', '/sessions/' + sessionId + '/payments/' + pid, patch);
}

export async function deletePayment(sessionId, pid) {
  return req('DELETE', '/sessions/' + sessionId + '/payments/' + pid);
}

// ── Attendance ────────────────────────────────────────────────────

export async function getAttendance(sessionId) {
  return req('GET', '/sessions/' + sessionId + '/attendance');
}

export async function saveAttendance(sessionId, date, cls, records) {
  return req('POST', '/sessions/' + sessionId + '/attendance', { date, cls, records });
}

// ── Exams ─────────────────────────────────────────────────────────

export async function getExams(sessionId) {
  return req('GET', '/sessions/' + sessionId + '/exams');
}

export async function createExam(sessionId, exam) {
  return req('POST', '/sessions/' + sessionId + '/exams', exam);
}

export async function updateExam(sessionId, eid, patch) {
  return req('PATCH', '/sessions/' + sessionId + '/exams/' + eid, patch);
}

export async function deleteExam(sessionId, eid) {
  return req('DELETE', '/sessions/' + sessionId + '/exams/' + eid);
}

// ── Admins ────────────────────────────────────────────────────────

export async function getAdmins() {
  return req('GET', '/admins');
}

export async function createAdmin(data) {
  return req('POST', '/admins', data);
}

export async function updateAdmin(id, patch) {
  return req('PATCH', '/admins/' + id, patch);
}

export async function removeAdmin(id) {
  return req('DELETE', '/admins/' + id);
}

// ── Load full session DB (assembles all collections into one object)
export async function loadSessionData(sessionId) {
  try {
    const safe = (p, fallback) => p.catch(e => { console.warn('[loadSessionData] partial fail:', e.message); return fallback; });

    const [config, students, teachers, payments, attendance, exams] = await Promise.all([
      safe(loadSessionConfig(sessionId), {}),
      safe(getStudents(sessionId),  []),
      safe(getTeachers(sessionId),  []),
      safe(getPayments(sessionId),  []),
      safe(getAttendance(sessionId), {}),
      safe(getExams(sessionId),     []),
    ]);

    // Normalise student/teacher photos stored separately in backend
    const photos = {};
    (Array.isArray(students) ? students : []).forEach(s => { if (s.photo) photos[s.sid] = s.photo; });
    const tphotos = {};
    (Array.isArray(teachers) ? teachers : []).forEach(t => { if (t.photo) tphotos[t.tid] = t.photo; });

    // Normalise marks
    const marks = {};
    (Array.isArray(exams) ? exams : []).forEach(e => {
      if (!e.marks || typeof e.marks !== 'object') return;
      // After .lean(), marks is a plain object { sid: value }
      const entries = Object.entries(e.marks);
      if (entries.length === 0) return;
      // Check if keys are encoded as "sid__cls" (multi-class) or plain "sid"
      const isMultiClass = entries.some(([k]) => k.includes('__'));
      if (isMultiClass) {
        // Decode: "sid__cls" → group by cls
        entries.forEach(([k, v]) => {
          const sep = k.lastIndexOf('__');
          const sid = k.slice(0, sep);
          const cls = k.slice(sep + 2);
          const marksKey = e.eid + '_' + cls;
          if (!marks[marksKey]) marks[marksKey] = {};
          marks[marksKey][sid] = v;
        });
      } else {
        // Single-class exam
        const cls = e.cls || '';
        const marksKey = e.eid + '_' + cls;
        marks[marksKey] = Object.fromEntries(entries);
      }
    });

    const defaultSettings = {
      school: 'LORD KRISHNA PUBLIC SCHOOL',
      year: '2025-2026',
      reportAcademicYear: '2025-2026',
      prin: '',
      phone: '',
      addr: '',
    };
    const rawSettings = config.settings || {};
    // Strip 'admin' placeholder from school address permanently
    if (rawSettings.addr && rawSettings.addr.trim() === 'admin') rawSettings.addr = '';
    return {
      settings: { ...defaultSettings, ...rawSettings },
      classes:  (config.classes  || []).map(c => { const { _id, __v, ...rest } = c; return rest; }),
      tt:       (config.tt       || []).map(({ _id, __v, ...r }) => r),
      slots:    (config.slots    || []).map(({ _id, __v, ...r }) => r),
      fstr: {
        months: (config.fstr?.months || []).map(({ _id, __v, ...r }) => r),
        extras: (config.fstr?.extras || []).map(({ _id, __v, ...r }) => r),
      },
      events:   (config.events   || []).map(({ _id, __v, ...r }) => r),
      certRegistry: (config.certRegistry || []).map(({ _id, __v, ...r }) => r),
      admins:   await getAdmins().catch(() => []),
      students: (Array.isArray(students) ? students : []).map(s => {
        // Strip 'admin' placeholder from address fields permanently at load time
        const cleanAddr = (v) => (v && v.trim() !== 'admin') ? v : '';
        return {
          ...s,
          id: s.sid,
          // Normalise all field aliases so web always reads canonical names
          // regardless of whether the record was created on web or mobile
          father: s.father || s.fa  || '',
          mother: s.mother || s.ma  || '',
          fphone: s.fphone || s.fp  || '',
          mphone: s.mphone || s.mp  || '',
          blood:  s.blood  || s.bl  || '',
          caste:    s.caste    || s.ca  || '',
          religion: s.religion || s.re  || '',
          city:   cleanAddr(s.city  || s.cy  || ''),
          aadhar: s.aadhar || s.aa  || '',
          cls:    s.cls    || s.cl  || '',
          roll:   s.roll   || s.ro  || '',
          admno:  s.admno  || s.an  || '',
          dob:    s.dob    || '',
          addr:   cleanAddr(s.addr  || ''),
          // Keep short-key aliases too so mobile can read them back
          fa: s.fa  || s.father  || '',
          ma: s.ma  || s.mother  || '',
          fp: s.fp  || s.fphone  || '',
          mp: s.mp  || s.mphone  || '',
          bl: s.bl  || s.blood   || '',
          ca: s.ca  || s.caste   || '',
          re: s.re  || s.religion || '',
          cy: cleanAddr(s.cy || s.city || ''),
          aa: s.aa  || s.aadhar  || '',
          ro: s.ro  || s.roll    || '',
          an: s.an  || s.admno   || '',
          cl: s.cl  || s.cls     || '',
          fe: s.fe  ?? s.fee     ?? 0,
          mf: s.mf  ?? 0,
        };
      }),
      teachers: (Array.isArray(teachers) ? teachers : []).map(t => ({ ...t, id: t.tid })),
      pays:     (Array.isArray(payments) ? payments : []).map(p => ({
        ...p,
        id:  p.pid,
        dt:  p.dt   || p.date || '',
        md:  p.md   || p.mode || 'Cash',
        mn:  p.mn   || (Array.isArray(p.months) ? p.months.join(', ') : ''),
      })),
      att:      (attendance && typeof attendance === 'object' && !Array.isArray(attendance)) ? attendance : {},
      exams:    (Array.isArray(exams) ? exams : []).map(e => ({
        ...e,
        id:  e.eid,
        // map backend field names → frontend field names
        su:  e.su  || e.subject || '',
        dt:  e.dt  || e.date    || '',
        max: e.max || e.maxMarks || 100,
      })),
      marks,
      photos,
      tphotos,
    };
  } catch (err) {
    console.error('loadSessionData failed:', err);
    return null;
  }
}

// ── Save full session DB (diffs and pushes only what changed) ─────
// We keep a shadow copy to detect changes
const _shadow = {};

// Call this after loadSessionData to prime the shadow so first save diffs correctly
export function initShadow(sessionId, db) {
  _shadow[sessionId] = JSON.parse(JSON.stringify(db));
}

export async function saveSessionData(sessionId, db) {
  const prev = _shadow[sessionId] || {};
  _shadow[sessionId] = JSON.parse(JSON.stringify(db));

  const ops = [];

  // Config fields — always save all of them on every call (they're small and diff is unreliable)
  const configPatch = {
    settings: db.settings,
    classes:  db.classes,
    tt:       db.tt,
    slots:    db.slots,
    fstr:     db.fstr,
    events:   db.events,
    certRegistry: db.certRegistry || [],
  };
  ops.push(saveSessionConfig(sessionId, configPatch));

  // Students — find added/updated/deleted
  const prevStudents = prev.students || [];
  const newStudents  = db.students   || [];
  const prevStuMap   = Object.fromEntries(prevStudents.map(s => [s.sid || s.id, s]));
  const newStuMap    = Object.fromEntries(newStudents.map(s => [s.sid || s.id, s]));

  newStudents.forEach(s => {
    const key = s.sid || s.id;
    // merge photo from photos map into the student record before saving
    const photo = (db.photos || {})[key] || (db.photos || {})[s.id] || null;
    // Always include photo field (null = explicitly cleared) so backend removes it
    // Also preserve any extra fields from the previous record (e.g. fields added by mobile)
    const prevRecord = prevStuMap[key] || {};
    const sWithPhoto = { ...prevRecord, ...s, sid: key, photo: photo || null };
    const prevPhoto = (prev.photos || {})[key] || (prev.photos || {})[prevStuMap[key]?.id] || null;
    const prevWithPhoto = prevStuMap[key]
      ? { ...prevStuMap[key], photo: prevPhoto }
      : null;
    if (!prevStuMap[key]) {
      ops.push(createStudent(sessionId, sWithPhoto));
    } else if (JSON.stringify(sWithPhoto) !== JSON.stringify(prevWithPhoto)) {
      ops.push(updateStudent(sessionId, key, sWithPhoto));
    }
  });
  prevStudents.forEach(s => {
    const key = s.sid || s.id;
    if (!newStuMap[key]) ops.push(deleteStudent(sessionId, key));
  });

  // Teachers
  const prevTeachers = prev.teachers || [];
  const newTeachers  = db.teachers   || [];
  const prevTeaMap   = Object.fromEntries(prevTeachers.map(t => [t.tid || t.id, t]));
  const newTeaMap    = Object.fromEntries(newTeachers.map(t => [t.tid || t.id, t]));

  newTeachers.forEach(t => {
    const key = t.tid || t.id;
    // merge photo from tphotos map into the teacher record before saving
    const photo = (db.tphotos || {})[key] || (db.tphotos || {})[t.id] || null;
    // Always include photo field (null = explicitly cleared) so backend removes it
    // Preserve any extra fields from the previous record
    const prevRecord = prevTeaMap[key] || {};
    const tWithPhoto = { ...prevRecord, ...t, tid: key, photo: photo || null };
    const prevPhoto = (prev.tphotos || {})[key] || (prev.tphotos || {})[prevTeaMap[key]?.id] || null;
    const prevWithPhoto = prevTeaMap[key]
      ? { ...prevTeaMap[key], photo: prevPhoto }
      : null;
    if (!prevTeaMap[key]) {
      ops.push(createTeacher(sessionId, tWithPhoto));
    } else if (JSON.stringify(tWithPhoto) !== JSON.stringify(prevWithPhoto)) {
      ops.push(updateTeacher(sessionId, key, tWithPhoto));
    }
  });
  prevTeachers.forEach(t => {
    const key = t.tid || t.id;
    if (!newTeaMap[key]) ops.push(deleteTeacher(sessionId, key));
  });

  // Payments
  const prevPays = prev.pays || [];
  const newPays  = db.pays   || [];
  const prevPayMap = Object.fromEntries(prevPays.map(p => [p.pid || p.id, p]));
  const newPayMap  = Object.fromEntries(newPays.map(p => [p.pid || p.id, p]));

  newPays.forEach(p => {
    const key = p.pid || p.id;
    if (!prevPayMap[key]) ops.push(createPayment(sessionId, {
      pid:      key,
      sid:      p.sid,
      amt:      p.amt,
      date:     p.dt   || p.date  || '',
      mode:     p.md   || p.mode  || 'Cash',
      note:     p.note || '',
      months:   p.mn   ? [p.mn]   : (p.months || []),
      extras:   p.extras || [],
      // preserve all extra frontend fields so they survive round-trip
      nm: p.nm, cls: p.cls, fee: p.fee, totalAmt: p.totalAmt,
      tuition: p.tuition, md: p.md, ref: p.ref, mn: p.mn,
      ty: p.ty, rc: p.rc, bb: p.bb, ba: p.ba,
      concession: p.concession, extrasConcession: p.extrasConcession,
      extrasRemaining: p.extrasRemaining, tuitionRemaining: p.tuitionRemaining,
      examFee: p.examFee, balanceRemaining: p.balanceRemaining, prevBalancePaid: p.prevBalancePaid, isPrevBalancePay: p.isPrevBalancePay,
    }));
    else {
      // Update existing payment if balanceRemaining changed (e.g. after previous balance payment)
      const prevPay = prevPayMap[key];
      if (prevPay && prevPay.balanceRemaining !== p.balanceRemaining) {
        ops.push(updatePayment(sessionId, key, { balanceRemaining: p.balanceRemaining }));
      }
    }
  });
  prevPays.forEach(p => {
    const key = p.pid || p.id;
    if (!newPayMap[key]) ops.push(deletePayment(sessionId, key));
  });

  // Exams
  const prevExams = prev.exams || [];
  const newExams  = db.exams  || [];
  const prevExMap = Object.fromEntries(prevExams.map(e => [e.eid || e.id, e]));
  const newExMap  = Object.fromEntries(newExams.map(e => [e.eid || e.id, e]));

  newExams.forEach(e => {
    const key = e.eid || e.id;
    // Collect all marks for this exam across all classes
    const allMarks = {};
    const classes = e.cls ? [e.cls] : (db.classes || []).map(c => c.name);
    classes.forEach(cls => {
      const marksKey = key + '_' + cls;
      const clsMarks = db.marks?.[marksKey] || {};
      Object.entries(clsMarks).forEach(([sid, val]) => {
        allMarks[sid + '__' + cls] = val; // encode cls into key for multi-class exams
      });
    });
    // For single-class exams, use simple sid keys
    const marksPayload = e.cls
      ? (db.marks?.[key + '_' + e.cls] || {})
      : allMarks;

    const payload = {
      ...e,
      eid:      key,
      subject:  e.su  || e.subject  || '',
      date:     e.dt  || e.date     || '',
      maxMarks: e.max || e.maxMarks || 100,
      marks:    marksPayload,
    };
    if (!prevExMap[key]) {
      ops.push(createExam(sessionId, payload));
    } else {
      const prevMarksKey = key + '_' + (e.cls || '');
      const prevMarks = prev.marks?.[prevMarksKey] || {};
      // Compare key fields to detect changes — include all academic exam fields
      const examFields = f => JSON.stringify({
        name: f.name, cls: f.cls, su: f.su || f.subject, dt: f.dt || f.date,
        max: f.max || f.maxMarks, term: f.term, startTime: f.startTime, endTime: f.endTime,
        rcKey: f.rcKey, divide: f.divide, isCoScho: f.isCoScho, acadTestId: f.acadTestId, st: f.st, suType: f.suType,
      });
      const examChanged = examFields(e) !== examFields(prevExMap[key]);
      const marksChanged = JSON.stringify(marksPayload) !== JSON.stringify(prevMarks);
      if (examChanged || marksChanged) {
        ops.push(updateExam(sessionId, key, payload));
      }
    }
  });
  prevExams.forEach(e => {
    const key = e.eid || e.id;
    if (!newExMap[key]) ops.push(deleteExam(sessionId, key));
  });

  // Attendance — find changed dates
  const prevAtt = prev.att || {};
  const newAtt  = db.att   || {};
  const allDates = new Set([...Object.keys(prevAtt), ...Object.keys(newAtt)]);

  // Build a map of student ID to class
  const studentClassMap = {};
  (db.students || []).forEach(s => {
    const classId = s.cls || s.cl;
    if (classId) {
      studentClassMap[s.id] = classId;
      studentClassMap[s.sid] = classId;
    }
  });

  allDates.forEach(date => {
    if (JSON.stringify(newAtt[date]) !== JSON.stringify(prevAtt[date]) && newAtt[date]) {
      // Group records by class
      const classRecords = {};
      Object.entries(newAtt[date]).forEach(([sid, status]) => {
        const cls = studentClassMap[sid] || '_all';
        if (!classRecords[cls]) classRecords[cls] = {};
        classRecords[cls][sid] = status;
      });
      // Save for each class that has records on this date
      Object.entries(classRecords).forEach(([cls, records]) => {
        // Only save if the records for this class have actually changed compared to prevAtt
        const prevClassRecords = {};
        Object.keys(records).forEach(sid => {
          if (prevAtt[date] && prevAtt[date][sid] !== undefined) {
            prevClassRecords[sid] = prevAtt[date][sid];
          }
        });
        if (JSON.stringify(records) !== JSON.stringify(prevClassRecords)) {
          ops.push(saveAttendance(sessionId, date, cls, records));
        }
      });
    }
  });

  await Promise.allSettled(ops);
}

export async function requestOTP() {
  return req('POST', '/auth/request-otp');
}

export async function verifyOTPChangePassword(otp, newPassword) {
  return req('POST', '/auth/verify-otp-change-password', { otp, newPassword });
}

export async function requestEmailOTP() {
  return req('POST', '/auth/request-email-otp');
}

export async function verifyCurrentEmailOTP(otp, newEmail) {
  return req('POST', '/auth/verify-current-email-otp', { otp, newEmail });
}

export async function verifyEmailOTP(otp, newEmail) {
  return req('POST', '/auth/verify-email-otp', { otp, newEmail });
}

export async function resetEmailWithPassword(password, newEmail) {
  return req('POST', '/auth/reset-email', { password, newEmail });
}

// ── Student credential recovery ───────────────────────────────────

export async function studentRecoverUsername(identifier) {
  return req('POST', '/auth/student-recover-username', { identifier });
}

export async function studentRequestOtp(identifier) {
  return req('POST', '/auth/student-request-otp', { identifier });
}

export async function studentVerifyOtp(identifier, otp) {
  return req('POST', '/auth/student-verify-otp', { identifier, otp });
}

export async function studentResetPassword(identifier, otp, newPassword) {
  return req('POST', '/auth/student-reset-password', { identifier, otp, newPassword });
}

export async function parentVerifyPassword(sid, password) {
  return req('POST', '/auth/parent-verify-password', { sid, password });
}

export async function parentChangePassword(currentPassword, newPassword) {
  return req('POST', '/auth/parent-change-password', { currentPassword, newPassword });
}

export async function studentRegisterEmail(email, otp) {  return req('POST', '/auth/student-register-email', { email, otp });
}

export async function studentRequestRegisterOtp(email) {
  return req('POST', '/auth/student-request-register-otp', { email });
}

export async function studentRequestChangeEmailOtp() {
  return req('POST', '/auth/student-request-change-email-otp');
}

export async function getStudentProfile() {
  return req('GET', '/auth/student-profile');
}

export async function studentVerifyCurrentEmailOtp(otp, newEmail) {
  return req('POST', '/auth/student-verify-current-email-otp', { otp, newEmail });
}

export async function studentConfirmNewEmail(otp) {
  return req('POST', '/auth/student-confirm-new-email', { otp });
}

export async function migrateLegacyData() { return; }
export async function saveSessions() { return; } // sessions managed via API

// ── Teacher credential recovery ───────────────────────────────────

export async function teacherRequestOtp(username) {
  return req('POST', '/auth/teacher-request-otp', { username });
}

export async function teacherRecoverUsername(email) {
  return req('POST', '/auth/teacher-recover-username', { email });
}

export async function teacherVerifyOtp(username, otp) {
  return req('POST', '/auth/teacher-verify-otp', { username, otp });
}

export async function teacherResetPassword(username, otp, newPassword) {
  return req('POST', '/auth/teacher-reset-password', { username, otp, newPassword });
}

export async function teacherUpdateEmail(email) {
  return req('POST', '/auth/teacher-update-email', { email });
}

export async function teacherRequestEmailOtp(email) {
  return req('POST', '/auth/teacher-request-email-otp', { email });
}

export async function teacherVerifyEmailOtp(otp) {
  return req('POST', '/auth/teacher-verify-email-otp', { otp });
}

export async function teacherRequestChangeEmailOtp() {
  return req('POST', '/auth/teacher-request-change-email-otp');
}

export async function teacherVerifyCurrentEmailOtp(otp, newEmail) {
  return req('POST', '/auth/teacher-verify-current-email-otp', { otp, newEmail });
}

export async function teacherConfirmNewEmail(otp) {
  return req('POST', '/auth/teacher-confirm-new-email', { otp });
}

export async function teacherChangePassword(currentPassword, newPassword) {
  return req('POST', '/auth/teacher-change-password', { currentPassword, newPassword });
}

// ── Messages (parent → admin) ─────────────────────────────────────

export async function getMessages(sessionId) {
  return req('GET', '/sessions/' + sessionId + '/messages');
}

export async function sendMessage(sessionId, msg) {
  return req('POST', '/sessions/' + sessionId + '/messages', msg);
}

export async function updateMessage(sessionId, id, patch) {
  return req('PATCH', '/sessions/' + sessionId + '/messages/' + id, patch);
}

export async function deleteMessage(sessionId, id) {
  return req('DELETE', '/sessions/' + sessionId + '/messages/' + id);
}
